/**
 * Syncs Loop subscription / one-time purchase with BNPL visibility and sticky ATC label.
 */

import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

export const PURCHASE_MODE_ATTR = 'data-purchase-mode';
export const PURCHASE_MODE_EVENT = 'purchase-mode:change';

/** @type {string | null} */
let lastMode = null;

/** @type {string | null} */
let lastLabel = null;

/** @type {string | null} */
let lastPricingSignature = null;

/**
 * @returns {HTMLFormElement | null}
 */
function getAddToCartForm() {
  const productForm = document.querySelector('product-form-component');
  const formInComponent = productForm?.querySelector('form[data-type="add-to-cart-form"]');
  if (formInComponent instanceof HTMLFormElement) return formInComponent;

  const fallback = document.querySelector('form[action*="/cart/add"]');
  return fallback instanceof HTMLFormElement ? fallback : null;
}

/**
 * @param {HTMLFormElement | null} form
 * @returns {HTMLInputElement | null}
 */
function getSellingPlanInput(form) {
  if (!form) return null;
  const input =
    form.querySelector('input[name="selling_plan"]') ||
    form.querySelector('input[name="selling_plan_id"]');
  return input instanceof HTMLInputElement ? input : null;
}

/**
 * @param {ParentNode} root
 * @param {(node: ParentNode) => void} visit
 */
function walkLoopSearchRoots(root, visit) {
  visit(root);
  if (root instanceof Element) {
    if (root.shadowRoot) visit(root.shadowRoot);
    root.querySelectorAll('shopify-app-block').forEach((block) => {
      visit(block);
      if (block.shadowRoot) visit(block.shadowRoot);
    });
  }
}

/**
 * @returns {HTMLInputElement | null}
 */
function findCheckedLoopRadio() {
  const details = document.querySelector('.product-details') ?? document;
  /** @type {HTMLInputElement | null} */
  let found = null;

  walkLoopSearchRoots(details, (root) => {
    if (found) return;
    const checked = root.querySelector(
      'input[type="radio"]:checked, [role="radio"][aria-checked="true"] input[type="radio"]'
    );
    if (checked instanceof HTMLInputElement) found = checked;
  });

  if (found) return found;

  walkLoopSearchRoots(document, (root) => {
    if (found) return;
    const checked = root.querySelector('shopify-app-block input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement) found = checked;
  });

  return found;
}

/**
 * @param {HTMLElement} el
 * @returns {HTMLElement}
 */
function findLoopOptionRow(el) {
  let node = el instanceof HTMLElement ? el : null;

  for (let depth = 0; depth < 10 && node; depth++) {
    const prices = parsePricesFromText(node.textContent ?? '');
    const hasCompare =
      node.querySelector(
        's, del, strike, [class*="compare"], [class*="strikethrough"], [class*="line-through"], [style*="line-through"]'
      ) != null;

    if (prices.length >= 2 || (prices.length >= 1 && hasCompare)) return node;
    node = node.parentElement;
  }

  return (
    (el instanceof HTMLElement && (el.closest('label') ?? el.closest('[role="radio"]'))) ||
    el.parentElement ||
    el
  );
}

/**
 * Compare-at amounts from struck-through / compare nodes inside the option row.
 * @param {HTMLElement} row
 * @returns {Set<number>}
 */
function getCompareAtAmounts(row) {
  /** @type {Set<number>} */
  const amounts = new Set();
  const compareNodes = row.querySelectorAll(
    's, del, strike, [class*="compare"], [class*="strikethrough"], [class*="line-through"], [style*="line-through"]'
  );

  for (const node of compareNodes) {
    for (const entry of parsePricesFromText(node.textContent ?? '')) {
      if (entry.amount > 0) amounts.add(entry.amount);
    }
  }

  return amounts;
}

/**
 * @param {HTMLElement} row
 * @param {boolean} isSubscription
 * @returns {string}
 */
function choosePriceFromLoopRow(row, isSubscription) {
  const parsed = parsePricesFromText(row.textContent ?? '');
  if (parsed.length === 0) return '';

  const compareAmounts = getCompareAtAmounts(row);
  const positive = parsed.filter((p) => p.amount > 0);
  const pool = positive.length > 0 ? positive : parsed;

  if (isSubscription) {
    const salePrices = pool.filter((p) => !compareAmounts.has(p.amount));
    const candidates = salePrices.length > 0 ? salePrices : pool;
    const chosen = candidates.reduce((min, p) => (p.amount < min.amount ? p : min));
    return chosen.token;
  }

  const retailPrices = pool.filter((p) => !compareAmounts.has(p.amount));
  const candidates = retailPrices.length > 0 ? retailPrices : pool;
  if (candidates.length === 1) {
    const only = candidates[0];
    return only ? only.token : '';
  }
  const chosen = candidates.reduce((max, p) => (p.amount > max.amount ? p : max));
  return chosen.token;
}

/**
 * @param {boolean} isSubscription
 * @returns {string}
 */
function readStickyPriceToken(isSubscription) {
  const checked = findCheckedLoopRadio();
  if (checked) {
    const row = findLoopOptionRow(checked);
    const fromRow = choosePriceFromLoopRow(row, isSubscription);
    if (fromRow) return fromRow;
  }

  const labelText = readPurchaseLabelFromChecked(checked);
  const fromLabel = choosePriceToken(labelText, isSubscription);
  if (fromLabel) return fromLabel;

  if (!isSubscription) return getMainDisplayedPrice();
  return '';
}

/**
 * @param {HTMLInputElement | null} checked
 * @returns {string}
 */
function readPurchaseLabelFromChecked(checked) {
  if (!(checked instanceof HTMLInputElement)) return '';

  const scope = document.querySelector('.product-details') ?? document;
  const labelledBy = checked.getAttribute('aria-labelledby');
  if (labelledBy) {
    const labelEl = document.getElementById(labelledBy);
    if (labelEl?.textContent?.trim()) return labelEl.textContent.trim();
  }

  const row = findLoopOptionRow(checked);
  if (row.textContent?.trim()) return row.textContent.trim().replace(/\s+/g, ' ');

  const label = checked.closest('label');
  if (label?.textContent?.trim()) return label.textContent.trim();

  const parentLabel = scope.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
  if (parentLabel?.textContent?.trim()) return parentLabel.textContent.trim();

  return '';
}

/**
 * @param {boolean} isSubscription
 * @returns {string}
 */
const STICKY_LABEL_SEPARATOR = ' — ';

/**
 * @param {boolean} isSubscription
 * @returns {string}
 */
function purchaseTypeTitle(isSubscription) {
  return isSubscription ? '' : 'One-time purchase';
}

/**
 * @param {string} text
 * @returns {{ token: string, amount: number }[]}
 */
function parsePricesFromText(text) {
  /** @type {{ token: string, amount: number }[]} */
  const entries = [];

  const western =
    text.match(/(?:[A-Z]{0,3}\$|[$€£¥])\s*\d[\d,]*(?:\.\d{1,2})?/g) ?? [];
  for (const token of western) {
    const amount = Number(token.replace(/[^\d.]/g, ''));
    if (Number.isFinite(amount)) entries.push({ token: token.trim(), amount });
  }

  const localized = text.match(/\d[\d.,]*\s*(?:VND|₫|đ)|(?:VND)\s*\d[\d.,]*/gi) ?? [];
  for (const token of localized) {
    const minor = convertMoneyToMinorUnits(token, 'VND');
    if (minor == null) continue;
    entries.push({ token: token.trim(), amount: minor });
  }

  return entries;
}

/**
 * Subscription → lowest non-zero price; one-time → highest non-zero price.
 * @param {string} text
 * @param {boolean} isSubscription
 * @returns {string}
 */
function choosePriceToken(text, isSubscription) {
  const parsed = parsePricesFromText(text);
  if (parsed.length === 0) return '';

  const nonZero = parsed.filter((p) => p.amount > 0);
  const pool = nonZero.length > 0 ? nonZero : parsed;

  if (pool.length === 1) {
    const only = pool[0];
    return only ? only.token : '';
  }

  const chosen = isSubscription
    ? pool.reduce((min, p) => (p.amount < min.amount ? p : min))
    : pool.reduce((max, p) => (p.amount > max.amount ? p : max));

  return chosen.token;
}

/**
 * @returns {string}
 */
function getMainDisplayedPrice() {
  const current = document.querySelector('variant-main-price-sync .price--current');
  const text = current?.textContent?.trim();
  return text && /\d/.test(text) ? text : '';
}

/**
 * @returns {string}
 */
function getMainDisplayedComparePrice() {
  const compare = document.querySelector('variant-main-price-sync .price--compare');
  const text = compare?.textContent?.trim();
  return text && /\d/.test(text) ? text : '';
}

/**
 * Locale catalog prices from Liquid (presentment), not Loop DOM or client money_format.
 * @param {HTMLElement | null | undefined} [root]
 * @returns {{ current: string, compare: string, priceAmount: number | null, compareAmount: number | null }}
 */
export function readCatalogFormattedPrices(root) {
  /**
   * @param {HTMLElement} el
   */

  
  const readFrom = (el) => {
   
  
    
    const priceRaw = el.dataset.catalogPrice;
    const compareRaw = el.dataset.catalogCompareAt;
    const priceAmount = priceRaw != null && priceRaw !== '' ? Number(priceRaw) : null;
    const compareAmount = compareRaw != null && compareRaw !== '' ? Number(compareRaw) : null;

    console.log({
      catalogPrice: el.dataset.catalogPrice,
      priceRaw,
      priceAmount,
    });

    return {
      current: (el.dataset.catalogPriceFormatted || '').trim(),
      compare: (el.dataset.catalogCompareFormatted || '').trim(),
      priceAmount: Number.isFinite(priceAmount) ? priceAmount : null,
      compareAmount: Number.isFinite(compareAmount) ? compareAmount : null,
    };
  };

  /** @type {HTMLElement | null} */
  const sticky =
    root instanceof HTMLElement && root.matches('[data-sticky-price]')
      ? root
      : document.querySelector('sticky-add-to-cart [data-sticky-price]');
  const sync = document.querySelector('variant-main-price-sync');

  const stickyData = sticky instanceof HTMLElement ? readFrom(sticky) : null;
  const syncData = sync instanceof HTMLElement ? readFrom(sync) : null;

  const priceAmount = stickyData?.priceAmount ?? syncData?.priceAmount ?? null;
  const compareAmount = stickyData?.compareAmount ?? syncData?.compareAmount ?? null;

  let current = stickyData?.current || syncData?.current || '';
  let compare = stickyData?.compare || syncData?.compare || '';

  if (!current) current = getMainDisplayedPrice();
  if (!compare) compare = getMainDisplayedComparePrice();

  return {
    current,
    compare,
    priceAmount,
    compareAmount,
  };
}

/**
 * Sticky floater format: `Autoship — $99.00` / `One-time purchase — $110.00`
 * @param {boolean} isSubscription
 * @returns {string}
 */
function formatStickyPurchaseLabel(isSubscription) {
  return purchaseTypeTitle(isSubscription);
}

/**
 * @returns {boolean | null} null when unknown
 */
function isSubscriptionFromLoopUi() {
  const scope = document.querySelector('.product-details') ?? document;
  const blocks = scope.querySelectorAll('shopify-app-block');

  for (const block of blocks) {
    const checked = block.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) continue;

    const value = (checked.value || '').toLowerCase();
    const name = (checked.name || '').toLowerCase();
    const id = (checked.id || '').toLowerCase();
    const haystack = `${value} ${name} ${id}`;

    if (
      haystack.includes('subscription') ||
      haystack.includes('subscribe') ||
      haystack.includes('selling_plan') ||
      haystack.includes('autoship')
    ) {
      return true;
    }

    const rowText = (checked.closest('label')?.textContent ?? checked.value ?? '').toLowerCase();
    if (rowText.includes('autoship') || rowText.includes('subscribe')) {
      return true;
    }

    if (
      haystack.includes('one-time') ||
      haystack.includes('onetime') ||
      haystack.includes('one_time') ||
      haystack.includes('single') ||
      haystack.includes('onetime')
    ) {
      return false;
    }
  }

  return null;
}

/**
 * @returns {boolean}
 */
export function resolveIsSubscriptionPurchase() {
  const form = getAddToCartForm();
  const sellingPlanInput = getSellingPlanInput(form);
  const sellingPlanValue = sellingPlanInput?.value?.trim() ?? '';
  const loopUiSubscription = isSubscriptionFromLoopUi();

  let isSubscription = sellingPlanValue.length > 0;
  if (!isSubscription && loopUiSubscription === true) {
    isSubscription = true;
  } else if (sellingPlanValue.length === 0 && loopUiSubscription === false) {
    isSubscription = false;
  }

  return isSubscription;
}

/**
 * @returns {string}
 */
function getShopMoneyFormat() {
  const boot = document.getElementById('discount-shop-bootstrap');
  return boot?.dataset.moneyFormat || '${{amount}}';
}

/**
 * @returns {string}
 */
function getShopCurrencyCode() {
  const boot = document.getElementById('discount-shop-bootstrap');
  return boot?.dataset.currency || 'USD';
}

/**
 * @param {number | null | undefined} cents
 * @param {HTMLElement | null | undefined} [stickyPriceEl]
 * @returns {string}
 */
function formatShopifyVariantMoney(cents, stickyPriceEl) {
  if (cents == null || !Number.isFinite(cents)) return '';

  const moneyFormat =
    (stickyPriceEl instanceof HTMLElement && stickyPriceEl.dataset.moneyFormat) ||
    getShopMoneyFormat();
  const currency =
    (stickyPriceEl instanceof HTMLElement && stickyPriceEl.dataset.currency) ||
    getShopCurrencyCode();
  const rounded = Math.round(cents);

  const shopify = /** @type {{ formatMoney?: (c: number, f: string) => string }} */ (window.Shopify);
  if (typeof shopify?.formatMoney === 'function') {
    try {
      return shopify.formatMoney(rounded, moneyFormat);
    } catch {
      /* use theme formatter */
    }
  }

  return formatMoney(rounded, moneyFormat, currency);
}

/**
 * @param {string} token
 * @returns {number | null}
 */
function parseDisplayedPriceToCents(token) {
  if (!token?.trim()) return null;
  return convertMoneyToMinorUnits(token.trim(), getShopCurrencyCode());
}

/**
 * @param {HTMLElement | null | undefined} stickyPriceEl
 * @returns {{ price: number | null, compareAt: number | null }}
 */
function readStickyVariantCentsFromDataset(stickyPriceEl) {
  if (!(stickyPriceEl instanceof HTMLElement)) {
    return { price: null, compareAt: null };
  }

  const priceRaw = stickyPriceEl.dataset.variantPrice;
  const compareRaw = stickyPriceEl.dataset.variantCompareAt;

  const price = priceRaw != null && priceRaw !== '' ? Number(priceRaw) : null;
  const compareAt = compareRaw != null && compareRaw !== '' ? Number(compareRaw) : null;

  return {
    price: Number.isFinite(price) ? price : null,
    compareAt: Number.isFinite(compareAt) ? compareAt : null,
  };
}

/**
 * Sticky floater + main PDP: locale catalog current/compare (Liquid). Subscription uses catalog only (not Loop DOM).
 * @param {HTMLElement | null | undefined} [stickyPriceEl]
 * @returns {{ isSubscription: boolean, currentToken: string, compareToken: string, discountPercent: number | null }}
 */
export function getStickyMobilePricing(stickyPriceEl) {
  const isSubscription = resolveIsSubscriptionPurchase();
  const catalog = readCatalogFormattedPrices(stickyPriceEl);

  const priceAmount = catalog.priceAmount;
  const compareAmount = catalog.compareAmount;
  const compareIsHigher =
    compareAmount != null && priceAmount != null && compareAmount > priceAmount;

  /** @type {number | null} */
  let discountPercent = null;
  if (compareIsHigher && compareAmount != null && priceAmount != null) {
    discountPercent = Math.round(((compareAmount - priceAmount) * 100) / compareAmount);
  }

  let currentToken = catalog.current;
  let compareToken = compareIsHigher && catalog.compare ? catalog.compare : '';

  if (!currentToken && priceAmount != null) {
    currentToken = formatShopifyVariantMoney(priceAmount, stickyPriceEl);
  }
  if (!compareToken && compareIsHigher && compareAmount != null) {
    compareToken = formatShopifyVariantMoney(compareAmount, stickyPriceEl);
  }

  return {
    isSubscription,
    currentToken: (currentToken || '').trim(),
    compareToken: (compareToken || '').trim(),
    discountPercent,
  };
}

function syncPurchaseMode() {
  const isSubscription = resolveIsSubscriptionPurchase();
  const mode = isSubscription ? 'subscription' : 'one-time';

  document.documentElement.setAttribute(PURCHASE_MODE_ATTR, mode);

  const label = formatStickyPurchaseLabel(isSubscription);
  const stickyPriceEl = document.querySelector('sticky-add-to-cart [data-sticky-price]');
  const stickyMobilePricing = getStickyMobilePricing(
    stickyPriceEl instanceof HTMLElement ? stickyPriceEl : null
  );
  const pricingSignature = `${stickyMobilePricing.compareToken}|${stickyMobilePricing.currentToken}|${stickyMobilePricing.discountPercent}|${mode}`;

  if (mode === lastMode && lastLabel === label && pricingSignature === lastPricingSignature) return;
  lastMode = mode;
  lastLabel = label;
  lastPricingSignature = pricingSignature;

  document.dispatchEvent(
    new CustomEvent(PURCHASE_MODE_EVENT, {
      detail: { mode, label, isSubscription, stickyMobilePricing },
    })
  );
}

function init() {
  syncPurchaseMode();

  const form = getAddToCartForm();
  if (form) {
    const observer = new MutationObserver(() => syncPurchaseMode());
    observer.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value', 'checked'],
    });
  }

  const details = document.querySelector('.product-details');
  if (details) {
    const detailsObserver = new MutationObserver(() => syncPurchaseMode());
    detailsObserver.observe(details, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['value', 'checked', 'aria-checked', 'data-selected'],
    });

    details.querySelectorAll('shopify-app-block').forEach((block) => {
      if (!block.shadowRoot) return;
      const shadowObserver = new MutationObserver(() => syncPurchaseMode());
      shadowObserver.observe(block.shadowRoot, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['value', 'checked', 'aria-checked', 'data-selected', 'class'],
      });
    });
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (
      target.matches('input[name="selling_plan"], input[name="selling_plan_id"]') ||
      target.closest('shopify-app-block') ||
      target.closest('[class*="loop"]')
    ) {
      syncPurchaseMode();
    }
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest('shopify-app-block') || target.closest('[class*="loop"]')) {
      requestAnimationFrame(() => syncPurchaseMode());
    }
  });

  document.addEventListener('variant:update', () => {
    requestAnimationFrame(() => syncPurchaseMode());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
